import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Mail, Phone, MapPin, Facebook, Instagram, Youtube, ArrowRight } from "lucide-react";
import { LOGO_URL, NAV_LINKS, CONTACT, BIBLE_VERSE } from "@/data/content";
import api from "@/lib/api";

const NewsletterForm = () => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setLoading(true);
    try {
      const { data } = await api.post("/newsletter", { email });
      toast.success(data.message || "Merci !");
      setEmail("");
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Erreur, veuillez réessayer.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={submit} className="mt-5 flex gap-2" data-testid="newsletter-form">
      <input
        type="email"
        required
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="Votre email"
        data-testid="newsletter-email"
        className="flex-1 min-w-0 px-4 py-3 bg-white/10 border border-white/20 text-white placeholder-blue-200 text-sm rounded-lg focus:outline-none focus:border-white focus:bg-white/15"
      />
      <button
        type="submit"
        disabled={loading}
        data-testid="newsletter-submit"
        aria-label="S'abonner"
        className="h-12 w-12 shrink-0 bg-white text-[#0B3D91] rounded-lg flex items-center justify-center hover:bg-blue-50 transition-colors disabled:opacity-60"
      >
        <ArrowRight size={18} />
      </button>
    </form>
  );
};

const Footer = () => {
  return (
    <footer
      data-testid="main-footer"
      className="bg-[#0B3D91] text-white"
    >
      <div className="mv-container py-20">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-5">
            <Link to="/" className="flex items-center gap-3">
              <img src={LOGO_URL} alt="MIREVSA" className="h-14 w-14 object-contain bg-white/10 rounded-md p-1" />
              <div>
                <div className="font-heading font-black text-2xl tracking-tight">MIREVSA</div>
                <div className="text-[10px] uppercase tracking-[0.18em] text-blue-200">La Quête des Âmes</div>
              </div>
            </Link>
            <p className="mt-6 text-blue-100 text-base leading-relaxed max-w-md">
              Organisation Chrétienne Internationale en Haïti — dédiée à l'évangélisation,
              à la formation biblique et à l'impact social durable.
            </p>
            <div className="mt-8">
              <div className="text-xs uppercase tracking-[0.2em] font-bold text-white">Newsletter</div>
              <p className="mt-2 text-sm text-blue-100">Recevez nos actualités et enseignements.</p>
              <NewsletterForm />
            </div>
            <blockquote className="mt-8 pl-4 border-l-2 border-white/40 italic text-blue-100">
              {BIBLE_VERSE}
            </blockquote>
          </div>

          <div className="md:col-span-3">
            <h3 className="font-heading font-bold text-sm uppercase tracking-[0.2em] mb-5 text-white">
              Liens rapides
            </h3>
            <ul className="space-y-3">
              {NAV_LINKS.map((l) => (
                <li key={l.to}>
                  <Link
                    to={l.to}
                    data-testid={`footer-link-${l.to.replace("/", "") || "home"}`}
                    className="text-blue-100 hover:text-white transition-colors text-sm"
                  >
                    {l.label}
                  </Link>
                </li>
              ))}
              <li>
                <Link to="/join" className="text-blue-100 hover:text-white transition-colors text-sm">
                  Rejoindre
                </Link>
              </li>
              <li>
                <Link to="/donation" className="text-blue-100 hover:text-white transition-colors text-sm">
                  Faire un don
                </Link>
              </li>
              <li>
                <Link to="/resources" className="text-blue-100 hover:text-white transition-colors text-sm">
                  Ressources
                </Link>
              </li>
              <li>
                <Link to="/missions" className="text-blue-100 hover:text-white transition-colors text-sm">
                  Missions
                </Link>
              </li>
            </ul>
          </div>

          <div className="md:col-span-4">
            <h3 className="font-heading font-bold text-sm uppercase tracking-[0.2em] mb-5 text-white">
              Contact
            </h3>
            <ul className="space-y-4 text-sm">
              <li className="flex items-start gap-3">
                <Mail size={18} className="text-blue-200 mt-0.5 shrink-0" />
                <a
                  href={`mailto:${CONTACT.email}`}
                  className="text-blue-100 hover:text-white transition-colors"
                >
                  {CONTACT.email}
                </a>
              </li>
              <li className="flex items-start gap-3">
                <Phone size={18} className="text-blue-200 mt-0.5 shrink-0" />
                <a
                  href={`tel:${CONTACT.phone}`}
                  className="text-blue-100 hover:text-white transition-colors"
                >
                  {CONTACT.phone}
                </a>
              </li>
              <li className="flex items-start gap-3">
                <MapPin size={18} className="text-blue-200 mt-0.5 shrink-0" />
                <span className="text-blue-100">{CONTACT.address}</span>
              </li>
            </ul>

            <div className="flex gap-3 mt-7">
              <a
                href="#"
                aria-label="Facebook"
                className="h-10 w-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
              >
                <Facebook size={18} />
              </a>
              <a
                href="#"
                aria-label="Instagram"
                className="h-10 w-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
              >
                <Instagram size={18} />
              </a>
              <a
                href="#"
                aria-label="YouTube"
                className="h-10 w-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
              >
                <Youtube size={18} />
              </a>
            </div>
          </div>
        </div>

        <div className="mt-16 pt-8 border-t border-white/15 flex flex-col md:flex-row justify-between gap-4 text-xs text-blue-200">
          <div>© {new Date().getFullYear()} MIREVSA — Tous droits réservés.</div>
          <div className="flex gap-6">
            <Link to="/admin/login" data-testid="footer-admin-link" className="hover:text-white transition-colors">
              Espace Admin
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
